import React, { useEffect, useState, useMemo } from "react";
import {
  Flame, Trophy, LayoutGrid, CalendarDays, Crown,
} from "lucide-react";
import api from "@/lib/api";
import { BossBattleModal } from "@/components/BossBattleModal";
import TrainingInquiryBanner from "@/components/TrainingInquiryBanner";
import MissionControlHeader from "@/components/roadmap/MissionControlHeader";
import ChallengePathMap from "@/components/roadmap/ChallengePathMap";

const WEEKS = [
  { label: "Week 1", title: "Foundations", days: [1, 7] },
  { label: "Week 2", title: "Core Data Cloud", days: [8, 15] },
  { label: "Week 3", title: "Advanced Techniques", days: [16, 22] },
  { label: "Week 4", title: "Capstone & Mastery", days: [23, 30] },
];

const CHECKPOINT_DAYS = [5, 10, 15, 20, 25];

function WeeklyLeaderboardWidget() {
  const [entries, setEntries] = useState([]);
  useEffect(() => {
    api.get("/leaderboard", { params: { period: "weekly", limit: 5 } }).then((res) => setEntries(res.data.leaderboard)).catch(() => {});
  }, []);
  if (!entries.length) return null;
  return (
    <div className="glass-panel rounded-2xl p-5 mb-8" data-testid="weekly-leaderboard-widget">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="w-4 h-4 text-[#FBBF24]" />
        <h2 className="font-heading font-semibold text-white text-sm">This Week's Top Learners</h2>
      </div>
      <div className="space-y-2">
        {entries.map((e) => (
          <div key={e.user_id} className={`flex items-center gap-3 text-sm px-3 py-2 rounded-lg ${e.is_me ? "bg-indigo-400/10 border border-indigo-400/30" : ""}`} data-testid={`weekly-leaderboard-row-${e.user_id}`}>
            <span className="w-5 text-xs font-bold text-slate-400">{e.rank === 1 ? <Crown className="w-4 h-4 text-[#FBBF24]" /> : `#${e.rank}`}</span>
            <span className="flex-1 truncate text-slate-200">{e.name}{e.is_me && <span className="text-indigo-400"> (You)</span>}</span>
            <span className="text-xs font-bold text-[#FBBF24]">{e.xp} XP</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StreakCalendarView({ challenges }) {
  const completedDates = useMemo(() => {
    const set = new Set();
    challenges.forEach((c) => { if (c.completed_at) set.add(c.completed_at.slice(0, 10)); });
    return set;
  }, [challenges]);

  const days = useMemo(() => {
    const arr = [];
    const today = new Date();
    for (let i = 34; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      arr.push(d.toISOString().slice(0, 10));
    }
    return arr;
  }, []);

  return (
    <div className="glass-panel rounded-2xl p-6" data-testid="streak-calendar-view">
      <div className="flex items-center gap-2 mb-5">
        <Flame className="w-4 h-4 text-orange-400" />
        <h2 className="font-heading font-semibold text-white text-sm">Last 35 Days of Activity</h2>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {days.map((d) => {
          const done = completedDates.has(d);
          return (
            <div
              key={d} title={d} data-testid={`streak-calendar-day-${d}`}
              className={`aspect-square rounded-md flex items-center justify-center text-[9px] ${done ? "bg-gradient-to-br from-indigo-500 to-violet-500 text-white font-bold" : "bg-white/5 text-slate-500"}`}
            >
              {new Date(d).getDate()}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-4 text-[11px] text-slate-400">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-gradient-to-br from-indigo-500 to-violet-500 inline-block" /> Completed a challenge</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-white/5 inline-block" /> No activity</span>
      </div>
    </div>
  );
}

export default function RoadmapPage() {
  const [challenges, setChallenges] = useState([]);
  const [checkpoints, setCheckpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("path");
  const [bossDay, setBossDay] = useState(null);

  const loadCheckpoints = () => api.get("/checkpoints/status").then((res) => setCheckpoints(res.data)).catch(() => {});

  useEffect(() => {
    Promise.all([api.get("/challenges"), loadCheckpoints()])
      .then(([cRes]) => setChallenges(cRes.data))
      .finally(() => setLoading(false));
  }, []);

  const checkpointByDay = useMemo(() => Object.fromEntries(checkpoints.map((c) => [c.day, c])), [checkpoints]);
  const currentDay = useMemo(() => challenges.find((c) => c.status === "unlocked")?.day, [challenges]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="roadmap-loading">
        <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="roadmap-page">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-400">Curriculum</span>
          <h1 className="font-heading text-3xl font-semibold text-white mt-2">30-Day Journey</h1>
          <p className="text-slate-400 text-sm mt-1">Complete each day's quiz with 80%+ to unlock the next challenge.</p>
        </div>
        <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
          <button onClick={() => setView("path")} data-testid="roadmap-view-path-btn" className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md transition-colors ${view === "path" ? "bg-indigo-500 text-white" : "text-slate-400 hover:text-white"}`}>
            <LayoutGrid className="w-3.5 h-3.5" /> Mission Map
          </button>
          <button onClick={() => setView("calendar")} data-testid="roadmap-view-calendar-btn" className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md transition-colors ${view === "calendar" ? "bg-indigo-500 text-white" : "text-slate-400 hover:text-white"}`}>
            <CalendarDays className="w-3.5 h-3.5" /> Calendar
          </button>
        </div>
      </div>

      <MissionControlHeader totalDays={challenges.length} completedCount={challenges.filter((c) => c.status === "completed").length} />

      <WeeklyLeaderboardWidget />

      <TrainingInquiryBanner track="data_cloud" />

      {view === "calendar" ? (
        <StreakCalendarView challenges={challenges} />
      ) : (
        <ChallengePathMap
          weeks={WEEKS.map((week) => ({ ...week, challenges: challenges.filter((c) => c.day >= week.days[0] && c.day <= week.days[1]) }))}
          checkpointByDay={checkpointByDay}
          checkpointDays={CHECKPOINT_DAYS}
          currentDay={currentDay}
          onOpenBoss={setBossDay}
          dayLinkPrefix="/challenge"
        />
      )}

      {bossDay && <BossBattleModal day={bossDay} onClose={() => { setBossDay(null); loadCheckpoints(); }} />}
    </div>
  );
}
